import { useCallback, useState } from 'react';
import axios from 'axios';
import { useCalculatorStore } from '../store/calculatorStore';
import {
  buildFilename,
  buildSavedConfig,
  downloadConfig,
  readConfigFile,
  type SavedConfig,
} from '../utils/configFile';

const API_BASE = '/api';

/**
 * Save/load a configuration as a local JSON file. Downloads to disk
 * (save) or reads a user-picked file (load). No server storage.
 */
export function useConfig() {
  const projectId = useCalculatorStore((s) => s.projectId);
  const params = useCalculatorStore((s) => s.params);
  const answers = useCalculatorStore((s) => s.answers);
  const setConfigName = useCalculatorStore((s) => s.setConfigName);
  const selectProject = useCalculatorStore((s) => s.selectProject);
  const loadConfig = useCalculatorStore((s) => s.loadConfig);
  const [error, setError] = useState<string | null>(null);

  const saveToFile = useCallback(
    (name: string): boolean => {
      if (!projectId) return false;
      setError(null);
      try {
        const cfg = buildSavedConfig({
          name,
          project_id: projectId,
          params,
          answers,
        });
        downloadConfig(cfg);
        setConfigName(name);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Save failed');
        return false;
      }
    },
    [projectId, params, answers, setConfigName],
  );

  const loadFromFile = useCallback(
    async (file: File): Promise<SavedConfig | null> => {
      setError(null);
      try {
        const cfg = await readConfigFile(file);
        // selectProject fetches the schema and moves to the questionnaire step.
        // loadConfig immediately overrides that to the calculator step with
        // the saved params + answers.
        await selectProject(cfg.project_id);
        loadConfig(cfg.params, cfg.answers);
        setConfigName(cfg.name);
        return cfg;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Load failed');
        return null;
      }
    },
    [selectProject, loadConfig, setConfigName],
  );

  return { saveToFile, loadFromFile, error };
}

/**
 * CSV export — posts current UI state to the backend, no save required.
 * PDF export is client-side via window.print.
 */
export function useExport() {
  const projectId = useCalculatorStore((s) => s.projectId);
  const schema = useCalculatorStore((s) => s.schema);
  const params = useCalculatorStore((s) => s.params);
  const answers = useCalculatorStore((s) => s.answers);
  const configName = useCalculatorStore((s) => s.configName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportCSV = useCallback(
    async (nameOverride?: string) => {
      if (!projectId) return;
      setLoading(true);
      setError(null);
      const name = nameOverride ?? configName ?? 'untitled';
      const projectLabel = schema?.project.name ?? projectId;
      try {
        const r = await axios.post(
          `${API_BASE}/projects/${projectId}/export/csv`,
          { params, answers: answers ?? {}, name },
          { responseType: 'blob' },
        );
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', buildFilename(name, projectLabel, 'csv'));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Export failed');
      } finally {
        setLoading(false);
      }
    },
    [projectId, schema, params, answers, configName],
  );

  return { exportCSV, loading, error };
}
